import {
  DownloadOutlined,
  FilterOutlined,
  PrinterOutlined,
  SearchOutlined
} from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography
} from "antd";
import dayjs from "dayjs";
import { useRef, useState } from "react";
import { usePageKeyboardShortcuts } from "../../shared/hooks/usePageKeyboardShortcuts";
import { transportApi } from "../../api/transportApi";
import { getApiErrorMessage } from "../../utils/apiError";
import { message } from "antd";
import {
  printConsignmentStationery,
  printGatePassStationery,
  printLorryHireStationery,
  printManifestStationery
} from "../../shared/printing/transportPrintTemplates";

type BookingReportRow = {
  id: number;
  cnmtNo: string;
  bookingDate: string;
  to: string;
  consignor: string;
  consignee: string;
  pkg: number;
  aWeight: number;
  cWeight: number;
  goodsName: string;
  paymentType: string;
  freightAmount: number;
  riskCharge: number;
  toPay: number;
  paid: number;
  tbb: number;
  privateMark?: string;
  eWayBillNo?: string;
  status: string;
};

type BookingReport = {
  year: number;
  month: number;
  destination?: string;
  totalRows: number;
  totalPackages: number;
  totalActualWeight: number;
  totalChargedWeight: number;
  totalFreight: number;
  totalToPay: number;
  totalPaid: number;
  totalTbb: number;
  rows: BookingReportRow[];
};

const MONTH_NAMES = [
  "", "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const PAYMENT_COLORS: Record<string, string> = {
  ToPay: "orange",
  Paid: "green",
  ToBeBilled: "blue",
  FOC: "purple"
};

const PAYMENT_LABELS: Record<string, string> = {
  ToPay: "TO PAY",
  Paid: "PAID",
  ToBeBilled: "TBB",
  FOC: "FOC"
};

export const BookingReportPage = () => {
  const [form] = Form.useForm();
  const [printForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [printLoading, setPrintLoading] = useState<string | null>(null);
  const [report, setReport] = useState<BookingReport | null>(null);
  const [search, setSearch] = useState("");
  const searchRef = useRef<any>(null);

  // Quick-print lookup state — label is plain string (shown in trigger); subtitle for rich dropdown
  type PrintOpt = { value: number; label: string; subtitle?: string };
  const [cnmtOpts,      setCnmtOpts]      = useState<PrintOpt[]>([]);
  const [cnmtSearching, setCnmtSearching] = useState(false);
  const [mfstOpts,      setMfstOpts]      = useState<PrintOpt[]>([]);
  const [mfstSearching, setMfstSearching] = useState(false);
  const [lhOpts,        setLhOpts]        = useState<PrintOpt[]>([]);
  const [lhSearching,   setLhSearching]   = useState(false);
  const [gpOpts,        setGpOpts]        = useState<PrintOpt[]>([]);
  const [gpSearching,   setGpSearching]   = useState(false);
  const cnmtTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mfstTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lhTimer   = useRef<ReturnType<typeof setTimeout> | null>(null);
  const gpTimer   = useRef<ReturnType<typeof setTimeout> | null>(null);

  const debounced = (
    timerRef: React.MutableRefObject<ReturnType<typeof setTimeout> | null>,
    setSearching: (v: boolean) => void,
    fn: (q: string) => Promise<void>,
  ) => (q: string) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!q.trim()) return;
    timerRef.current = setTimeout(async () => {
      setSearching(true);
      try { await fn(q); } catch { /* ignore */ } finally { setSearching(false); }
    }, 280);
  };

  const searchCnmt = debounced(cnmtTimer, setCnmtSearching, async (q) => {
    const res = await transportApi.searchConsignmentLookups(q, 1, 15);
    setCnmtOpts(res.items.map(item => ({ value: item.id, label: item.label, subtitle: item.subtitle })));
  });

  const searchMfst = debounced(mfstTimer, setMfstSearching, async (q) => {
    const res = await transportApi.listManifests({ q, pageSize: 15 });
    setMfstOpts(res.data.map((m: any) => ({
      value: m.id,
      label: m.manifestNo,
      subtitle: `${m.vehicleNo}  ·  ${dayjs(m.dispatchDate).format("DD-MMM-YY")}`,
    })));
  });

  const searchLh = debounced(lhTimer, setLhSearching, async (q) => {
    const res = await transportApi.listLorryHireChallans({ q, pageSize: 15 });
    setLhOpts((res.data ?? []).map((h: any) => ({
      value: h.id,
      label: h.challanNo,
      subtitle: `${h.vehicle?.vehicleNo ?? "—"}  ·  ${dayjs(h.challanDate).format("DD-MMM-YY")}`,
    })));
  });

  const searchGp = debounced(gpTimer, setGpSearching, async (q) => {
    const res = await transportApi.searchGatePasses(q, 1, 15);
    setGpOpts((res.data ?? []).map((g: any) => ({
      value: g.id,
      label: g.gatePassNo,
      subtitle: `${g.type}  ·  Mfst: ${g.manifestNo}  ·  ${dayjs(g.gateTime).format("DD-MMM-YY")}`,
    })));
  });

  // Rich dropdown renderer — shows document no bold + contextual details
  const renderPrintOpt = (opt: { data: unknown }) => {
    const o = opt.data as PrintOpt;
    return (
      <div style={{ lineHeight: 1.4 }}>
        <span style={{ fontWeight: 600 }}>{o.label}</span>
        {o.subtitle && <span style={{ fontSize: 11, opacity: 0.65, marginLeft: 6 }}>{o.subtitle}</span>}
      </div>
    );
  };

  usePageKeyboardShortcuts({
    "f5":    () => void runReport(),
    "f3":    () => searchRef.current?.focus(),
    "ctrl+enter": () => void runReport(),
  });

  const runReport = async () => {
    const values = await form.validateFields();
    const month = values.month as dayjs.Dayjs;
    setLoading(true);
    try {
      const data = await transportApi.getBookingReport({
        year: month.year(),
        month: month.month() + 1,
        destination: values.destination?.trim() || undefined
      });
      setReport(data);
      setSearch("");
    } catch (error) {
      message.error(getApiErrorMessage(error, "Failed to load booking report"));
    } finally {
      setLoading(false);
    }
  };

  const exportCsv = () => {
    if (!report) return;
    const headers = ["Sr#", "CnmtNo", "Date", "To", "Consignor", "Consignee", "Pkg", "AWeight", "CWeight", "GoodsName", "Payment", "Freight", "RiskCharge", "ToPay", "Paid", "TBB"];
    const rows = filteredRows.map((r, i) => [
      i + 1, r.cnmtNo, dayjs(r.bookingDate).format("DD-MMM-YY"), r.to,
      r.consignor, r.consignee, r.pkg, r.aWeight, r.cWeight,
      r.goodsName, PAYMENT_LABELS[r.paymentType] ?? r.paymentType,
      r.freightAmount, r.riskCharge, r.toPay, r.paid, r.tbb
    ]);
    const csv = [headers, ...rows].map(row => row.map(v => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `booking-report-${report.year}-${String(report.month).padStart(2, "0")}${report.destination ? `-${report.destination}` : ""}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filteredRows = (report?.rows ?? []).filter(row => {
    const term = search.trim().toLowerCase();
    if (!term) return true;
    return [row.cnmtNo, row.to, row.consignor, row.consignee, row.goodsName].some(v =>
      (v ?? "").toLowerCase().includes(term)
    );
  });

  const printDocument = async (type: "consignment" | "manifest" | "lorryHire" | "gatePass") => {
    const values = await printForm.validateFields([type]);
    const id = Number(values[type]);
    if (!id) {
      message.warning("Select a document to print");
      return;
    }
    setPrintLoading(type);
    try {
      if (type === "consignment") {
        printConsignmentStationery(await transportApi.getConsignmentPrintReport(id));
      } else if (type === "manifest") {
        printManifestStationery(await transportApi.getManifestReport(id));
      } else if (type === "lorryHire") {
        printLorryHireStationery(await transportApi.getLorryHirePrintReport(id));
      } else {
        printGatePassStationery(await transportApi.getGatePassPrintReport(id));
      }
    } catch (error) {
      message.error(getApiErrorMessage(error, "Unable to print document"));
    } finally {
      setPrintLoading(null);
    }
  };

  return (
    <Card className="panel-card">
      <div className="panel-title-row">
        <Space align="center" size={8}>
          <Typography.Title level={4} style={{ margin: 0 }}>Booking Report</Typography.Title>
          <span className="kbd-hint"><kbd>Ctrl</kbd>+<kbd>Shift</kbd>+<kbd>B</kbd></span>
        </Space>
        <Space>
          {report && (
            <>
              <Button icon={<DownloadOutlined />} onClick={exportCsv}>Export CSV</Button>
              <Button icon={<PrinterOutlined />} onClick={() => window.print()}>Print</Button>
            </>
          )}
        </Space>
      </div>

      {/* Document Quick-Print */}
      <Card size="small" style={{ marginBottom: 16 }} title="Document Quick-Print">
        <Form form={printForm} layout="vertical" size="small">
          <Row gutter={[12, 8]}>

            {/* Consignment – search by Bilty / LR No */}
            <Col xs={24} sm={12}>
              <Form.Item
                name="consignment"
                label="Bilty / LR No"
                style={{ marginBottom: 0 }}
                rules={[{ required: true, message: "Select a consignment" }]}
              >
                <Space.Compact style={{ width: "100%" }}>
                  <Select
                    size="small"
                    showSearch allowClear filterOption={false}
                    loading={cnmtSearching}
                    placeholder="Search bilty / LR no…"
                    style={{ flex: 1, minWidth: 0 }}
                    options={cnmtOpts}
                    onSearch={searchCnmt}
                    notFoundContent={cnmtSearching ? "Searching…" : "Type bilty or LR no to search"}
                    optionRender={renderPrintOpt}
                  />
                  <Button
                    size="small"
                    icon={<PrinterOutlined />}
                    loading={printLoading === "consignment"}
                    onClick={() => void printDocument("consignment")}
                  >
                    Print Bilty
                  </Button>
                </Space.Compact>
              </Form.Item>
            </Col>

            {/* Manifest – search by Manifest No */}
            <Col xs={24} sm={12}>
              <Form.Item
                name="manifest"
                label="Manifest No"
                style={{ marginBottom: 0 }}
                rules={[{ required: true, message: "Select a manifest" }]}
              >
                <Space.Compact style={{ width: "100%" }}>
                  <Select
                    size="small"
                    showSearch allowClear filterOption={false}
                    loading={mfstSearching}
                    placeholder="Search manifest no…"
                    style={{ flex: 1, minWidth: 0 }}
                    options={mfstOpts}
                    onSearch={searchMfst}
                    notFoundContent={mfstSearching ? "Searching…" : "Type manifest no to search"}
                    optionRender={renderPrintOpt}
                  />
                  <Button
                    size="small"
                    icon={<PrinterOutlined />}
                    loading={printLoading === "manifest"}
                    onClick={() => void printDocument("manifest")}
                  >
                    Print Manifest
                  </Button>
                </Space.Compact>
              </Form.Item>
            </Col>

            {/* Lorry Hire – search by Challan No */}
            <Col xs={24} sm={12}>
              <Form.Item
                name="lorryHire"
                label="Lorry Hire Challan No"
                style={{ marginBottom: 0 }}
                rules={[{ required: true, message: "Select a challan" }]}
              >
                <Space.Compact style={{ width: "100%" }}>
                  <Select
                    size="small"
                    showSearch allowClear filterOption={false}
                    loading={lhSearching}
                    placeholder="Search challan no…"
                    style={{ flex: 1, minWidth: 0 }}
                    options={lhOpts}
                    onSearch={searchLh}
                    notFoundContent={lhSearching ? "Searching…" : "Type challan no to search"}
                    optionRender={renderPrintOpt}
                  />
                  <Button
                    size="small"
                    icon={<PrinterOutlined />}
                    loading={printLoading === "lorryHire"}
                    onClick={() => void printDocument("lorryHire")}
                  >
                    Print LH Challan
                  </Button>
                </Space.Compact>
              </Form.Item>
            </Col>

            {/* Gate Pass – search by Gate Pass No */}
            <Col xs={24} sm={12}>
              <Form.Item
                name="gatePass"
                label="Gate Pass No"
                style={{ marginBottom: 0 }}
                rules={[{ required: true, message: "Select a gate pass" }]}
              >
                <Space.Compact style={{ width: "100%" }}>
                  <Select
                    size="small"
                    showSearch allowClear filterOption={false}
                    loading={gpSearching}
                    placeholder="Search gate pass no…"
                    style={{ flex: 1, minWidth: 0 }}
                    options={gpOpts}
                    onSearch={searchGp}
                    notFoundContent={gpSearching ? "Searching…" : "Type gate pass no to search"}
                    optionRender={renderPrintOpt}
                  />
                  <Button
                    size="small"
                    icon={<PrinterOutlined />}
                    loading={printLoading === "gatePass"}
                    onClick={() => void printDocument("gatePass")}
                  >
                    Print Gate Pass
                  </Button>
                </Space.Compact>
              </Form.Item>
            </Col>

          </Row>
        </Form>
      </Card>

      {/* Filter bar */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Form form={form} layout="inline" initialValues={{ month: dayjs() }}>
          <Form.Item name="month" label="Month / Year" rules={[{ required: true, message: "Select month" }]}>
            <DatePicker picker="month" format="MMM YYYY" style={{ width: 160 }} />
          </Form.Item>
          <Form.Item name="destination" label="Destination Gate">
            <Input placeholder="e.g. IMPHAL, GUW…" style={{ width: 200 }} allowClear />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              icon={<FilterOutlined />}
              loading={loading}
              onClick={() => void runReport()}
            >
              Run Report <span className="kbd-hint" style={{ marginLeft: 4 }}><kbd>F5</kbd></span>
            </Button>
          </Form.Item>
        </Form>
      </Card>

      {report && (
        <>
          {/* Report header */}
          <div style={{ marginBottom: 16 }}>
            <Typography.Title level={5} style={{ margin: 0 }}>
              BOOKING REPORT{report.destination ? ` — FOR ${report.destination.toUpperCase()}` : ""} &nbsp;
              <Typography.Text type="secondary" style={{ fontSize: 14, fontWeight: "normal" }}>
                MONTH {MONTH_NAMES[report.month]?.toUpperCase()}, {report.year}
              </Typography.Text>
            </Typography.Title>
          </div>

          {/* Summary stats */}
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={4}><Statistic title="Consignments" value={report.totalRows} /></Col>
            <Col span={4}><Statistic title="Total Packages" value={report.totalPackages} /></Col>
            <Col span={4}><Statistic title="Actual Weight" value={report.totalActualWeight} suffix="kg" /></Col>
            <Col span={4}><Statistic title="Charged Weight" value={report.totalChargedWeight} suffix="kg" /></Col>
            <Col span={4}><Statistic title="To Pay" value={report.totalToPay} prefix="₹" precision={2} /></Col>
            <Col span={4}><Statistic title="Paid" value={report.totalPaid} prefix="₹" precision={2} /></Col>
          </Row>

          <Divider style={{ margin: "8px 0 12px" }} />

          {/* Search within results */}
          <Space style={{ marginBottom: 12 }}>
            <Input
              ref={searchRef}
              className="panel-search-input"
              prefix={<SearchOutlined />}
              placeholder="Search within report…  F3"
              allowClear
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ width: 320 }}
            />
            <Typography.Text type="secondary">
              {filteredRows.length} of {report.rows.length} rows
            </Typography.Text>
          </Space>

          <Table
            rowKey="id"
            size="small"
            loading={loading}
            dataSource={filteredRows}
            scroll={{ x: 1400 }}
            pagination={{ pageSize: 50, showSizeChanger: true, pageSizeOptions: ["25", "50", "100", "200"] }}
            summary={() => (
              <Table.Summary.Row style={{ fontWeight: 600, background: "var(--app-bg)" }}>
                <Table.Summary.Cell index={0} colSpan={2}>TOTAL</Table.Summary.Cell>
                <Table.Summary.Cell index={2} />
                <Table.Summary.Cell index={3} />
                <Table.Summary.Cell index={4} />
                <Table.Summary.Cell index={5} />
                <Table.Summary.Cell index={6} align="right">
                  {filteredRows.reduce((s, r) => s + r.pkg, 0)}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={7} align="right">
                  {filteredRows.reduce((s, r) => s + r.aWeight, 0).toFixed(0)}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={8} align="right">
                  {filteredRows.reduce((s, r) => s + r.cWeight, 0).toFixed(0)}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={9} />
                <Table.Summary.Cell index={10} align="right">
                  {filteredRows.reduce((s, r) => s + r.toPay, 0).toFixed(2)}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={11} align="right">
                  {filteredRows.reduce((s, r) => s + r.paid, 0).toFixed(2)}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={12} align="right">
                  {filteredRows.reduce((s, r) => s + r.tbb, 0).toFixed(2)}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={13} align="right">
                  {filteredRows.reduce((s, r) => s + r.riskCharge, 0).toFixed(2)}
                </Table.Summary.Cell>
              </Table.Summary.Row>
            )}
            columns={[
              { title: "Sr#", width: 50, render: (_, __, i) => i + 1 },
              { title: "Cnmt No", dataIndex: "cnmtNo", width: 130 },
              {
                title: "Date",
                dataIndex: "bookingDate",
                width: 100,
                render: (v: string) => dayjs(v).format("DD-MMM-YY")
              },
              { title: "To", dataIndex: "to", width: 110 },
              { title: "Consignor", dataIndex: "consignor", ellipsis: true },
              { title: "Consignee", dataIndex: "consignee", ellipsis: true },
              { title: "Pkg", dataIndex: "pkg", width: 55, align: "right" },
              { title: "AWt", dataIndex: "aWeight", width: 70, align: "right" },
              { title: "CWt", dataIndex: "cWeight", width: 70, align: "right" },
              { title: "Goods", dataIndex: "goodsName", width: 120, ellipsis: true },
              {
                title: "To Pay",
                dataIndex: "toPay",
                width: 80,
                align: "right",
                render: (v: number) => v > 0 ? v.toFixed(0) : ""
              },
              {
                title: "Paid",
                dataIndex: "paid",
                width: 80,
                align: "right",
                render: (v: number) => v > 0 ? v.toFixed(0) : ""
              },
              {
                title: "TBB",
                dataIndex: "tbb",
                width: 80,
                align: "right",
                render: (v: number) => v > 0 ? v.toFixed(0) : ""
              },
              {
                title: "Risk",
                dataIndex: "riskCharge",
                width: 70,
                align: "right",
                render: (v: number) => v > 0 ? v.toFixed(0) : ""
              },
              {
                title: "Pay",
                dataIndex: "paymentType",
                width: 70,
                align: "center",
                render: (v: string) => (
                  <Tag color={PAYMENT_COLORS[v] ?? "default"} style={{ fontSize: 10, padding: "0 4px" }}>
                    {PAYMENT_LABELS[v] ?? v}
                  </Tag>
                )
              },
              {
                title: "E-Way",
                dataIndex: "eWayBillNo",
                width: 110,
                ellipsis: true,
                render: (v?: string) => v || ""
              }
            ]}
          />
        </>
      )}

      {!report && !loading && (
        <div style={{ textAlign: "center", padding: "48px 0", color: "#9ca3af" }}>
          Select a month and click <strong>Run Report</strong> to view the booking report.
        </div>
      )}
    </Card>
  );
};
